import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Edit2, X, LogOut, Upload, Package, Users, FolderPlus, Tag } from "lucide-react";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BulkDiscount from "../components/BulkDiscount";
import { toast } from "sonner";

const empty = {
  name: "", category: "", subcategory: "",
  brand: "MileForge", price: 0, compare_price: null, description: "",
  features: [], specifications: {}, images: [], stock: 100, featured: false,
  payment_link: "",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [featuresStr, setFeaturesStr] = useState("");
  const [imagesStr, setImagesStr] = useState("");
  const [specsStr, setSpecsStr] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", tagline: "", image: "", firstSub: "" });
  const [addingSub, setAddingSub] = useState(false);
  const [newSub, setNewSub] = useState("");

  const load = () => api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data));
  const loadCategories = () => api.get("/categories").then((r) => {
    setCategories(r.data);
    if (!form.category && r.data.length) setForm((f) => ({ ...f, category: r.data[0].slug, subcategory: r.data[0].subs[0] || "" }));
  });
  useEffect(() => { load(); loadCategories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p });
    setFeaturesStr((p.features || []).join("\n"));
    setImagesStr((p.images || []).join("\n"));
    setSpecsStr(Object.entries(p.specifications || {}).map(([k, v]) => `${k}: ${v}`).join("\n"));
  };

  const reset = () => {
    setEditing(null);
    setForm({ ...empty, category: categories[0]?.slug || "", subcategory: categories[0]?.subs?.[0] || "" });
    setFeaturesStr(""); setImagesStr(""); setSpecsStr("");
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price),
      compare_price: form.compare_price ? Number(form.compare_price) : null,
      stock: Number(form.stock),
      features: featuresStr.split("\n").map((s) => s.trim()).filter(Boolean),
      images: imagesStr.split("\n").map((s) => s.trim()).filter(Boolean),
      specifications: Object.fromEntries(specsStr.split("\n").map((l) => {
        const idx = l.indexOf(":");
        if (idx < 0) return null;
        return [l.slice(0, idx).trim(), l.slice(idx + 1).trim()];
      }).filter(Boolean)),
    };
    try {
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      reset(); load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted"); load();
  };

  const cat = categories.find((c) => c.slug === form.category);

  const createCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) return;
    try {
      const r = await api.post("/categories", {
        name: newCategory.name.trim(),
        tagline: newCategory.tagline.trim(),
        image: newCategory.image.trim(),
        subs: newCategory.firstSub.trim() ? [newCategory.firstSub.trim()] : [],
      });
      toast.success(`Category "${r.data.name}" added`);
      setNewCategory({ name: "", tagline: "", image: "", firstSub: "" });
      setAddingCategory(false);
      await loadCategories();
      setForm((f) => ({ ...f, category: r.data.slug, subcategory: r.data.subs[0] || "" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create category");
    }
  };

  const createSubcategory = async (e) => {
    e.preventDefault();
    if (!newSub.trim() || !cat) return;
    try {
      const r = await api.post(`/categories/${cat.id}/subcategories`, { name: newSub.trim() });
      toast.success(`Subcategory "${newSub.trim()}" added`);
      setNewSub(""); setAddingSub(false);
      await loadCategories();
      setForm((f) => ({ ...f, subcategory: newSub.trim() }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add subcategory");
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"? Products already using it will keep the old value.`)) return;
    try {
      await api.delete(`/categories/${category.id}`);
      toast.success("Category deleted");
      const r = await api.get("/categories");
      setCategories(r.data);
      setForm((f) => ({ ...f, category: r.data[0]?.slug || "", subcategory: r.data[0]?.subs?.[0] || "" }));
    } catch {
      toast.error("Could not delete category");
    }
  };

  const deleteSubcategory = async (subName) => {
    if (!cat) return;
    if (!window.confirm(`Delete subcategory "${subName}"?`)) return;
    try {
      await api.delete(`/categories/${cat.id}/subcategories/${encodeURIComponent(subName)}`);
      toast.success("Subcategory deleted");
      const r = await api.get("/categories");
      setCategories(r.data);
      const freshCat = r.data.find((c) => c.slug === form.category);
      setForm((f) => ({ ...f, subcategory: freshCat?.subs?.[0] || "" }));
    } catch {
      toast.error("Could not delete subcategory");
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(r.data.url);
      }
      const merged = (imagesStr ? imagesStr + "\n" : "") + urls.join("\n");
      setImagesStr(merged);
      toast.success(`Uploaded ${urls.length} image(s)`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="overline">Admin · {user?.email}</div>
          <h1 className="section-title text-3xl sm:text-4xl">Product Manager</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/admin/orders" data-testid="admin-nav-orders" className="btn-ghost"><Package size={16} /> Orders</Link>
          <Link to="/admin/users" data-testid="admin-nav-users" className="btn-ghost"><Users size={16} /> Registered Users</Link>
          <BulkDiscount />
          <button data-testid="admin-new" onClick={reset} className="btn-orange"><Plus size={16} /> New Product</button>
          <button data-testid="admin-logout" onClick={logout} className="btn-ghost"><LogOut size={16} /> Logout</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <form onSubmit={save} className="lg:col-span-5 border border-white/10 p-6 space-y-4 h-fit lg:sticky lg:top-28">
          <h3 className="font-display uppercase tracking-widest text-sm">{editing ? "Edit Product" : "New Product"}</h3>

          <input data-testid="admin-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full bg-[#111] border border-white/15 px-3 py-2.5 focus:outline-none focus:border-[#D96A1B]" />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <select data-testid="admin-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: categories.find(c => c.slug === e.target.value)?.subs[0] || "" })} className="w-full bg-[#111] border border-white/15 px-3 py-2.5">
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              {!addingCategory ? (
                <div className="mt-1.5 flex items-center gap-3">
                  <button type="button" data-testid="admin-add-category-toggle" onClick={() => setAddingCategory(true)} className="text-[11px] text-[#D96A1B] hover:underline flex items-center gap-1"><FolderPlus size={12} /> New Category</button>
                  {cat && (
                    <button type="button" data-testid="admin-delete-category" onClick={() => deleteCategory(cat)} className="text-[11px] text-white/40 hover:text-red-400 flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  )}
                </div>
              ) : (
                <div className="mt-2 border border-white/10 p-3 space-y-2 bg-[#0a0a0a]">
                  <input data-testid="new-category-name" required value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} placeholder="Category name" className="w-full bg-[#111] border border-white/15 px-2.5 py-2 text-xs" />
                  <input data-testid="new-category-tagline" value={newCategory.tagline} onChange={(e) => setNewCategory({ ...newCategory, tagline: e.target.value })} placeholder="Tagline (shown on homepage)" className="w-full bg-[#111] border border-white/15 px-2.5 py-2 text-xs" />
                  <input data-testid="new-category-image" value={newCategory.image} onChange={(e) => setNewCategory({ ...newCategory, image: e.target.value })} placeholder="Image URL (shown on homepage)" className="w-full bg-[#111] border border-white/15 px-2.5 py-2 text-xs" />
                  <input data-testid="new-category-first-sub" value={newCategory.firstSub} onChange={(e) => setNewCategory({ ...newCategory, firstSub: e.target.value })} placeholder="First subcategory (optional)" className="w-full bg-[#111] border border-white/15 px-2.5 py-2 text-xs" />
                  <div className="flex gap-2">
                    <button type="button" data-testid="new-category-save" onClick={createCategory} className="btn-orange text-xs py-1.5 px-3">Add Category</button>
                    <button type="button" onClick={() => setAddingCategory(false)} className="btn-ghost text-xs py-1.5 px-3">Cancel</button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <select data-testid="admin-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="w-full bg-[#111] border border-white/15 px-3 py-2.5">
                {(cat?.subs || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {!addingSub ? (
                <div className="mt-1.5 flex items-center gap-3">
                  <button type="button" data-testid="admin-add-subcategory-toggle" onClick={() => setAddingSub(true)} disabled={!cat} className="text-[11px] text-[#D96A1B] hover:underline flex items-center gap-1 disabled:opacity-40"><Tag size={12} /> New Subcategory</button>
                  {form.subcategory && (
                    <button type="button" data-testid="admin-delete-subcategory" onClick={() => deleteSubcategory(form.subcategory)} className="text-[11px] text-white/40 hover:text-red-400 flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  )}
                </div>
              ) : (
                <div className="mt-2 border border-white/10 p-3 space-y-2 bg-[#0a0a0a]">
                  <input data-testid="new-subcategory-name" required value={newSub} onChange={(e) => setNewSub(e.target.value)} placeholder="Subcategory name" className="w-full bg-[#111] border border-white/15 px-2.5 py-2 text-xs" />
                  <div className="flex gap-2">
                    <button type="button" data-testid="new-subcategory-save" onClick={createSubcategory} className="btn-orange text-xs py-1.5 px-3">Add</button>
                    <button type="button" onClick={() => setAddingSub(false)} className="btn-ghost text-xs py-1.5 px-3">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input data-testid="admin-price" required type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price" className="bg-[#111] border border-white/15 px-3 py-2.5" />
          </div>

          <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="3" placeholder="Description" className="w-full bg-[#111] border border-white/15 px-3 py-2.5" />

          <textarea value={featuresStr} onChange={(e) => setFeaturesStr(e.target.value)} rows="3" placeholder="Features (one per line)" className="w-full bg-[#111] border border-white/15 px-3 py-2.5" />

          <div className="border border-white/10 p-3 bg-[#0a0a0a]">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Product Images</div>
              <label className="flex items-center gap-2 cursor-pointer text-xs font-display tracking-widest uppercase text-[#D96A1B] hover:text-[#E87A2A]">
                <Upload size={14} />
                {uploading ? "Uploading..." : "Upload Files"}
                <input
                  ref={fileInputRef}
                  data-testid="admin-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
            </div>
            <textarea value={imagesStr} onChange={(e) => setImagesStr(e.target.value)} rows="3" placeholder="Image URLs (one per line) — or upload above" className="w-full bg-[#111] border border-white/15 px-3 py-2.5 text-xs" />
            {imagesStr && (
              <div className="flex gap-2 mt-3 overflow-x-auto">
                {imagesStr.split("\n").filter(Boolean).slice(0, 6).map((src, i) => (
                  <img key={src + i} src={src} alt="" className="w-16 h-20 object-cover border border-white/10" />
                ))}
              </div>
            )}
          </div>

          <textarea value={specsStr} onChange={(e) => setSpecsStr(e.target.value)} rows="3" placeholder="Specs (key: value per line)" className="w-full bg-[#111] border border-white/15 px-3 py-2.5" />

          <div className="grid grid-cols-2 gap-3">
            <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock" className="bg-[#111] border border-white/15 px-3 py-2.5" />
            <label className="flex items-center gap-2 text-sm font-body"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#D96A1B]" /> Featured</label>
          </div>

          <div>
            <input
              data-testid="admin-payment-link"
              type="url"
              value={form.payment_link || ""}
              onChange={(e) => setForm({ ...form, payment_link: e.target.value })}
              placeholder="Payment Link (Razorpay / UPI / GPay page URL) — optional"
              className="w-full bg-[#111] border border-white/15 px-3 py-2.5"
            />
            <p className="mt-1.5 text-[11px] text-white/40 font-body">
              Paste the payment link you created externally (Razorpay Page, PayU, UPI deep-link, WhatsApp Pay). If set, a <span className="text-[#D96A1B]">Buy Now</span> button will open this link for this product.
            </p>
          </div>

          <div className="flex gap-2">
            <button data-testid="admin-save" className="btn-orange flex-1 justify-center">{editing ? "Update" : "Create"}</button>
            {editing && <button type="button" onClick={reset} className="btn-ghost"><X size={14} /></button>}
          </div>
        </form>

        <div className="lg:col-span-7 space-y-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`admin-row-${p.slug}`} className="flex items-center gap-4 border border-white/10 p-3 hover:border-[#D96A1B]/50">
              <img src={p.images?.[0]} alt="" className="w-14 h-16 object-cover" />
              <div className="flex-1">
                <div className="font-display uppercase tracking-tight text-sm">{p.name}</div>
                <div className="text-xs text-white/50">{p.category} · {p.subcategory} · Stock {p.stock} {p.featured && "· ★"} {p.payment_link && <span className="text-[#D96A1B]">· 💳 link</span>}</div>
              </div>
              <div className="font-display text-[#D96A1B]">{formatINR(p.price)}</div>
              <button onClick={() => startEdit(p)} className="p-2 text-white/60 hover:text-white" data-testid={`admin-edit-${p.slug}`}><Edit2 size={14} /></button>
              <button onClick={() => del(p.id)} className="p-2 text-white/60 hover:text-[#D96A1B]" data-testid={`admin-delete-${p.slug}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
